import React from 'react';
import { Box, Checkbox, FormControlLabel, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import { BaseResource } from '../../types';
import { createSubtleIceSurface } from '../../theme/surfaces';
import { ConfirmActionDialog } from './ConfirmActionDialog';

const surfaceSx = createSubtleIceSurface(500, {});

interface BaseResourceCardProps {
  resource: BaseResource;
  onRemove: (refundCost: boolean) => void;
}

export const BaseResourceCard: React.FC<BaseResourceCardProps> = React.memo(function BaseResourceCard({
  resource,
  onRemove,
}) {
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [refundCost, setRefundCost] = React.useState(true);

  return (
    <Box sx={{ ...surfaceSx, p: 2, borderRadius: 1 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.5}>
        <Typography variant="subtitle2">{resource.name}</Typography>
        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ ml: 0.5, mt: -0.5 }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Stack>
      {resource.cost != null && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Cost: {resource.cost}gc
        </Typography>
      )}
      {resource.description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {resource.description}
        </Typography>
      )}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { setMenuAnchor(null); setRefundCost(true); setConfirmOpen(true); }}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Remove</ListItemText>
        </MenuItem>
      </Menu>
      <ConfirmActionDialog
        open={confirmOpen}
        title="Remove Resource"
        message={`Remove ${resource.name} from your base?`}
        confirmLabel="Remove"
        confirmColor="error"
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => { setConfirmOpen(false); onRemove(refundCost); }}
      >
        {resource.cost != null && resource.cost > 0 ? (
          <FormControlLabel
            control={<Checkbox checked={refundCost} onChange={(e) => setRefundCost(e.target.checked)} size="small" />}
            label={`Refund ${resource.cost}gc to warband`}
            sx={{ mt: 1, display: 'block' }}
          />
        ) : null}
      </ConfirmActionDialog>
    </Box>
  );
});
